

import { formatCurrencyFull, formatDate } from "@/lib/data";
import { getImageUrl } from "@/lib/utils";

interface PrintableQuotationProps {
  quotation: any;
  businessInfo: {
    name: string;
    logoUrl?: string;
    tagline?: string;
    address?: string;
    phone?: string;
    email?: string;
    gstNumber?: string;
  };
}

export default function PrintableQuotation({ quotation, businessInfo }: PrintableQuotationProps) {
  const services = quotation.items || quotation.quotation_services || [];
  const vendors = quotation.vendors_data || quotation.quotation_vendors || [];
  const clientName = quotation.client?.name || "—";
  
  const discountValue = quotation.discount_value || 0;
  const discountAmt = quotation.discount_type === 'percentage'
    ? quotation.subtotal * (discountValue / 100)
    : discountValue;
  const afterDiscount = Math.max(0, quotation.subtotal - discountAmt);
  const taxPercent = quotation.tax || 0;
  const taxAmt = Math.round(afterDiscount * (taxPercent / 100));

  return (
    <div id="print-quotation-container" className="print-container hidden print:block">
      <div className="print-page">
        {/* Header with Gold Accent */}
        <div className="print-header-accent" />
        
        {/* Business Header — Option A: Logo · Name+Tagline · Number box */}
        <div className="print-header">
          <div style={{ display: "flex", alignItems: "center", gap: "5mm", marginBottom: "3mm" }}>
            {/* Logo */}
            {businessInfo.logoUrl && (
              <div style={{ flexShrink: 0 }}>
                <img
                  src={getImageUrl(businessInfo.logoUrl)}
                  alt="Logo"
                  className="print-logo"
                  style={{ height: "55px", maxWidth: "130px", objectFit: "contain", display: "block" }}
                />
              </div>
            )}

            {/* Business name + tagline — centered */}
            <div style={{ flex: 1, textAlign: "center" }}>
              <p style={{ fontSize: "18pt", fontWeight: 800, color: "#111827", letterSpacing: "-0.3px", lineHeight: 1.1, marginBottom: "1.5mm" }}>
                {businessInfo.name.toUpperCase()}
              </p>
              <p style={{ fontSize: "10pt", color: "#C9A84C", fontWeight: 600 }}>
                {businessInfo.tagline || "BANQUET HALL & EVENT SERVICES"}
              </p>
            </div>

            {/* Quotation number box */}
            <div className="print-invoice-number" style={{ flexShrink: 0, padding: "1.5mm 2.5mm", textAlign: "right" }}>
              <p style={{ fontSize: "6pt", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.8px", color: "#6b7280", marginBottom: "0.5mm" }}>
                Quotation No.
              </p>
              <p style={{ fontSize: "11pt", fontWeight: 800, color: "#C9A84C", lineHeight: 1.1 }}>
                {quotation.quotation_number || `QT-${String(quotation.id).padStart(4, "0")}`}
              </p>
              <p style={{ fontSize: "6.5pt", marginTop: "0.5mm" }}>
                <span style={{ fontWeight: 700, color: "#059669" }}>
                  {(quotation.status || "draft").toUpperCase()}
                </span>
              </p>
            </div>
          </div>

          {/* Contact info — address line, then contact details line */}
          <div style={{ textAlign: "center", fontSize: "8pt", color: "#6b7280", lineHeight: 1.6, paddingBottom: "2mm" }}>
            {businessInfo.address && (
              <p>{businessInfo.address}</p>
            )}
            <p>
              {[
                businessInfo.phone ? `Phone: ${businessInfo.phone}` : null,
                businessInfo.email ? `Email: ${businessInfo.email}` : null,
                businessInfo.gstNumber ? `GSTIN: ${businessInfo.gstNumber}` : null,
              ].filter(Boolean).join("  •  ")}
            </p>
          </div>
        </div>

        <div className="print-divider" />

        {/* Quotation Info Cards — inline flex for reliable print layout */}
        <div style={{ display: "flex", justifyContent: "space-between", width: "100%", marginBottom: "6mm", gap: "4mm" }}>
          {[
            { label: "Client", value: clientName },
            { label: "Event Type", value: quotation.event_type || "—" },
            { label: "Event Date", value: quotation.event_date ? formatDate(quotation.event_date) : "—" },
            { label: "Guests", value: String(quotation.guests || quotation.guest_count || "—") },
          ].map((card, i) => (
            <div
              key={i}
              style={{
                flex: "1 1 0",
                textAlign: "center",
                padding: "2mm 1mm",
                borderLeft: i > 0 ? "1px solid #e5e7eb" : "none",
              }}
            >
              <p className="print-info-card-label">{card.label}</p>
              <p className="print-info-card-value">{card.value}</p>
            </div>
          ))}
        </div>

        {/* Items Table */}
        <div className="mb-4">
          {(services.length > 0 || vendors.length > 0) && (
            <>
              <div className="bg-stone-100 px-3 py-2 mb-2 border-l-4 border-amber-500">
                <h3 className="text-xs font-bold text-stone-900 uppercase tracking-wider">Items</h3>
              </div>
              <table className="print-table">
                <thead>
                  <tr>
                    <th className="text-left">Description</th>
                    <th className="text-center w-16">Type</th>
                    <th className="text-center w-16">QTY</th>
                    <th className="text-right w-28">Rate</th>
                    <th className="text-right w-28">Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {services.map((item: any, idx: number) => {
                    const servicePrice = item.unit_price || item.selling_price || item.base_price || item.price || 0;
                    return (
                      <tr key={`service-${idx}`}>
                        <td className="font-medium text-stone-800">{item.description || item.service_name || item.name || "Service"}</td>
                        <td className="text-center text-xs text-stone-500 uppercase">Service</td>
                        <td className="text-center text-stone-700">{item.quantity || 1}</td>
                        <td className="text-right text-stone-600">
                          {servicePrice > 0 ? formatCurrencyFull(servicePrice) : '—'}
                        </td>
                        <td className="text-right font-bold text-stone-900">
                          {formatCurrencyFull((item.quantity || 1) * servicePrice)}
                        </td>
                      </tr>
                    );
                  })}
                  {vendors.map((vendor: any, idx: number) => {
                    const vendorPrice = vendor.unit_price || vendor.cost || vendor.price || 0;
                    return (
                      <tr key={`vendor-${idx}`}>
                        <td className="font-medium text-stone-800">{vendor.description || vendor.vendor_name || vendor.name || "Vendor"}</td>
                        <td className="text-center text-xs text-stone-500 uppercase">Vendor</td>
                        <td className="text-center text-stone-700">{vendor.quantity || 1}</td>
                        <td className="text-right text-stone-600">
                          {vendorPrice > 0 ? formatCurrencyFull(vendorPrice) : '—'}
                        </td>
                        <td className="text-right font-bold text-stone-900">
                          {formatCurrencyFull((vendor.quantity || 1) * vendorPrice)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          )}

          {/* Summary Table */}
          <table className="print-table print-summary-table">
            <tbody>
              <tr className="border-t-2 border-stone-300">
                <td colSpan={4} className="text-right font-semibold text-stone-700">SUBTOTAL</td>
                <td className="text-right font-bold text-stone-900 w-28">{formatCurrencyFull(quotation.subtotal)}</td>
              </tr>
              {discountAmt > 0 && (
                <tr>
                  <td colSpan={4} className="text-right text-stone-600">
                    DISCOUNT {quotation.discount_type === 'percentage' ? `(${discountValue}%)` : ''}
                  </td>
                  <td className="text-right text-red-600 font-semibold w-28">-{formatCurrencyFull(discountAmt)}</td>
                </tr>
              )}
              {taxAmt > 0 && (
                <tr>
                  <td colSpan={4} className="text-right text-stone-600">TAX ({taxPercent}%)</td>
                  <td className="text-right font-semibold w-28">{formatCurrencyFull(taxAmt)}</td>
                </tr>
              )}
              <tr className="print-total-row">
                <td colSpan={4} className="text-right font-bold text-lg">TOTAL AMOUNT</td>
                <td className="text-right font-bold text-lg w-28">{formatCurrencyFull(quotation.total)}</td>
              </tr>
            </tbody>
          </table>
        </div>

        {/* Special Notes */}
        {quotation.notes && (
          <div className="print-notes-box mb-8">
            <h3 className="text-sm font-bold text-stone-900 mb-2 uppercase tracking-wide">Special Notes</h3>
            <p className="text-xs text-stone-700 whitespace-pre-wrap leading-relaxed">{quotation.notes}</p>
          </div>
        )}

        {/* Terms & Conditions */}
        <div className="mb-8">
          <h3 className="text-sm font-bold text-stone-900 mb-3 uppercase tracking-wide border-b border-stone-300 pb-2">Terms & Conditions</h3>
          <ul className="text-xs text-stone-700 space-y-1.5 leading-relaxed">
            <li>• <span className="font-semibold">Payment Terms:</span> 50% advance required to confirm booking, balance due 7 days before the event.</li>
            <li>• <span className="font-semibold">Cancellation Policy:</span> Cancellations made 30+ days before event are refundable. Cancellations within 30 days forfeit 50% of payment.</li>
            <li>• <span className="font-semibold">Validity:</span> This quotation is valid for 30 days from the date of issue.</li>
            <li>• <span className="font-semibold">Acceptance:</span> Acceptance of this quotation constitutes agreement to all terms and conditions.</li>
          </ul>
        </div>

        {/* Footer */}
        <div className="print-footer">
          <div>
            <p className="font-bold text-sm text-stone-900">Thank you for choosing {businessInfo.name}!</p>
            <p className="text-xs text-stone-600 mt-1.5">
              <span className="font-semibold">For queries:</span> {businessInfo.email || "info@shalimar.com"} | {businessInfo.phone || "+91-XXXX-XXXX"}
            </p>
          </div>
          <div className="text-right text-xs text-stone-500">
            <p className="font-semibold text-amber-600">Generated on</p>
            <p className="mt-0.5">{formatDate(new Date())}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
